import type { CliErrorCode, PromptRequiredError, SelectionRequiredError } from '../errors'
import { CliError } from '../errors'
import { cliCommand } from '../utils/invocation'
import { blankLine, sayError } from './output'

// One place that turns a thrown error into what the user reads and the code the shell sees.
//
// The errors themselves carry the fact ("this project has a login but no workspace attached"); the sentence
// that tells someone what to do about it is a CLI concern and lives here. Keeping the two apart is what lets
// the same operation be called from a program that has its own idea of how to report a problem.

const ADVICE: Partial<Record<CliErrorCode, (error: CliError) => string>> = {
  FR_NOT_LOGGED_IN: () =>
    'Deploying needs a connection to a Flowrunner workspace, and this project has none yet.\n' +
    `  Run \`${ cliCommand() } login\` first — it needs a browser, so it cannot be done from here.`,
  FR_NO_WORKSPACE: () =>
    'This project has a login but no workspace attached, so there is nowhere to deploy to.\n' +
    `  Run \`${ cliCommand() } login\` to pick one.`,
  FR_TOKEN_EXPIRED: () => `Session expired. Run \`${ cliCommand() } login\` and deploy again.`,
  FR_NO_SERVICES_DIR: () =>
    `No services/ directory in this project. Run \`${ cliCommand() } cs\` to create a service first.`,
  FR_NOT_A_PROJECT: () =>
    'There is no flowrunner.json here, so this is not a Flowrunner project.\n' +
    `  Run \`${ cliCommand() } init\` to create one.`,
  FR_NO_SERVICES: () =>
    'No services found under services/. Each one needs a src/index.js (or dist/index.js) ' +
    'that calls Flowrunner.createExtension.',
  // A deploy touches only the services it names, so which ones is a question and not a default.
  FR_SELECTION_REQUIRED: error =>
    `${ error.message }\n` +
    `  ${ cliCommand() } deploy ${ (error as SelectionRequiredError).available.map(id => `-s ${ id }`).join(' ') }\n` +
    `  ${ cliCommand() } deploy --all`,
  FR_AI_DOCS_MISSING: error =>
    `${ error.message }\n` +
    '  Upgrade the CLI in this project (`npm install -D flowrunner-cli@latest`) and try again.',
  // The operation states what it could not ask for; the flags that would have answered it are the CLI's
  // own vocabulary, so they are added here rather than thrown from inside the operation.
  FR_PROMPT_REQUIRED: error => {
    const fix = (error as PromptRequiredError).promptName === 'confirm-target'
      ? 'Pass --yes to initialise a project there anyway.'
      : 'Pass --yes to accept the defaults, or supply the values as flags.'

    return `${ error.message }\n  ${ fix }`
  },
}

/** Print the failure and end the process. Never returns. */
export function fail(error: unknown): never {
  const message = error instanceof CliError
    ? (ADVICE[error.code]?.(error) ?? error.message)
    : (error as Error).message

  blankLine()
  sayError(`  ${ message }`)
  sayError('')

  return process.exit(1)
}
